import { fabric } from "fabric";
import anime from "animejs";

import CanvasHandler from "./CanvasHandler";
import { FabricObject, FabricEvent, FabricGroup } from "../types/FabricCustomType";
import { isTouchInteraction } from "../utils/InteractionUtil";
import {
  enrichObjectEvent,
  wasDraggedForSelection,
  getDraggingSelectionRect
} from "../utils/ObjectUtil";
import { VideoObject } from "../canvas/fabric/Video";
import { code } from "../constants";

/**
 * Event CanvasHandler Class
 * @class EventHandler
 */
class EventHandler {
  canvasHandler: CanvasHandler;
  code: string;
  panning: boolean;
  startX = 0;
  startY = 0;

  private pinchZoomStartDistance = 0;
  private pinchZoomStartZoom = 1;
  private isPinching = false;
  private hasMoved = false;
  private MOVE_THRESHOLD = 10; // píxeles
  private objectHoverTimeout: number | null = null;
  private objectHoverEvent: FabricEvent<MouseEvent | TouchEvent> | null = null;

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
    this.initialize();
  }

  /**
   * Attch event on document
   *
   */
  public initialize() {
    if (this.canvasHandler.editable) {
      this.canvasHandler.canvas.on({
        "object:modified": this.modified,
        "object:scaling": this.scaling,
        "object:scaled": this.scaled,
        "object:moving": this.moving,
        "object:moved": this.moved,
        "object:rotating": this.rotating,
        "object:rotated": this.rotated,
        "mouse:wheel": this.mouseWheel,
        "mouse:down": this.mouseDown,
        "mouse:move": this.mouseMove,
        "mouse:up": this.mouseUp,
        "mouse:dblclick": this.mouseDblClick,
        "selection:cleared": this.selection,
        "selection:created": this.selection,
        "selection:updated": this.selection,
      });
    } else {
      this.canvasHandler.canvas.on({
        "mouse:down": this.mouseDown,
        "mouse:move": this.mouseMove,
        "mouse:up": this.mouseUp,
        "mouse:wheel": this.mouseWheel,
        "selection:cleared": this.selection,
        "selection:created": this.selection,
        "selection:updated": this.selection,
      });
    }

    const wrapper = this.canvasHandler.canvas.wrapperEl;
    // Evento para ocultar el hover/popup cuando el mouse sale del canvas
    this.canvasHandler.canvas.wrapperEl.addEventListener("mouseleave", this.cancelObjectHover);
    // Evento para ocultar el hover/popup cuando el mouse sale de la ventana del navegador
    window.addEventListener("blur", this.cancelObjectHover);
    // Evento para detectar cuando el mouse sale completamente de la ventana (no solo del div)
    window.addEventListener("mouseout", (e: any) => {
      // Si relatedTarget es null, el mouse salió de la ventana
      if (!e.relatedTarget && !e.toElement) {
        this.cancelObjectHover();
      }
    });
    wrapper.addEventListener("touchstart", this.onTouchStart, { passive: false });
    wrapper.addEventListener("touchmove", this.onTouchMove, { passive: false });
    wrapper.addEventListener("touchend", this.onTouchEnd, { passive: false });
    wrapper.addEventListener("touchcancel", this.onTouchEnd, { passive: false });

    wrapper.tabIndex = 1000;
    wrapper.addEventListener(
      "keydown",
      this.keyDown,
      false
    );
    wrapper.addEventListener("keyup", this.keyUp, false);
    wrapper.addEventListener(
      "mousedown",
      this.onMouseDown,
      false
    );
    wrapper.addEventListener(
      "contextmenu",
      this.contextMenu,
      false
    );
    if (this.canvasHandler.keyEvent.clipboard) {
      document.addEventListener("paste", this.paste, false);
    }
  }

  /**
   * Detach event on document
   *
   */
  public destroy = () => {
    if (this.canvasHandler.editable) {
      this.canvasHandler.canvas.off({
        "object:modified": this.modified,
        "object:scaling": this.scaling,
        "object:moving": this.moving,
        "object:moved": this.moved,
        "object:rotating": this.rotating,
        "mouse:wheel": this.mouseWheel,
        "mouse:down": this.mouseDown,
        "mouse:move": this.mouseMove,
        "mouse:up": this.mouseUp,
        "selection:cleared": this.selection,
        "selection:created": this.selection,
        "selection:updated": this.selection,
      });
    } else {
      this.canvasHandler.canvas.off({
        "mouse:down": this.mouseDown,
        "mouse:move": this.mouseMove,
        "mouse:up": this.mouseUp,
        "mouse:wheel": this.mouseWheel,
        "selection:cleared": this.selection,
        "selection:created": this.selection,
        "selection:updated": this.selection,
      });
      this.canvasHandler.getObjects().forEach((object) => {
        if (object.anime) {
          anime.remove(object);
        }
      });
    }
    this.canvasHandler.canvas.wrapperEl.removeEventListener("keydown", this.keyDown);
    this.canvasHandler.canvas.wrapperEl.removeEventListener("keyup", this.keyUp);
    this.canvasHandler.canvas.wrapperEl.removeEventListener(
      "mousedown",
      this.onMouseDown
    );
    this.canvasHandler.canvas.wrapperEl.removeEventListener(
      "contextmenu",
      this.contextMenu
    );
    if (this.canvasHandler.keyEvent.clipboard) {
      this.canvasHandler.canvas.wrapperEl.removeEventListener("paste", this.paste);
    }
  };

  /**
   * Dejar de usuar
   *
   */
  public object = {
    /**
     * Mouse double click event on object
     * @param {FabricEvent} opt
     */
    mousedblclick: (opt: FabricEvent) => {
      const { target } = opt;
      if (target) {
        const { onDblClick } = this.canvasHandler;
        if (onDblClick) {
          onDblClick(this.canvasHandler.canvas, target);
        }
      }
    },
  };

  /**
   * Modified event object
   *
   * @param {FabricEvent} opt
   * @returns
   */
  public modified = (opt: FabricEvent) => {
    const { target } = opt;
    if (!target) {
      return;
    }
    if (target.type === "ellipse" && target.parentId) {
      return;
    }
    const { onModified } = this.canvasHandler;
    if (onModified) {
      onModified(target);
    }
  };

  /**
   * Moving event object
   *
   * @param {FabricEvent} opt
   * @returns
   */
  public moving = (opt: FabricEvent) => {
    const { target } = opt as any;
    if (this.canvasHandler.interactionMode === "crop") {
      this.canvasHandler.cropHandler.moving(opt);
    } else {
      if (this.canvasHandler.editable && this.canvasHandler.guidelineOption.enabled) {
        this.canvasHandler.guidelineHandler.movingGuidelines(target);
      }
      if (target.type === "activeSelection") {
        const activeSelection = target as fabric.ActiveSelection;
        activeSelection.getObjects().forEach((obj: any) => {
          const left = target.left + obj.left + target.width / 2;
          const top = target.top + obj.top + target.height / 2;
          if (obj.superType === "node") {
            this.canvasHandler.portHandler.setCoords({ ...obj, left, top });
          } else if (obj.superType === "element") {
            const { id } = obj;
            const el = this.canvasHandler.elementHandler.findById(id);
            // TODO... Element object incorrect position
            this.canvasHandler.elementHandler.setPositionByOrigin(el, obj, left, top);
          }
        });
        return;
      }
      if (target.superType === "node") {
        this.canvasHandler.portHandler.setCoords(target);
      } else if (target.superType === "element") {
        const { id } = target;
        const el = this.canvasHandler.elementHandler.findById(id);
        this.canvasHandler.elementHandler.setPosition(el, target);
      }
    }
  };

  /**
   * Moved event object
   *
   * @param {FabricEvent} opt
   */
  public moved = (opt: FabricEvent) => {
    const { target } = opt;
    this.canvasHandler.gridHandler.setCoords(target);
    if (!this.canvasHandler.transactionHandler.active) {
      this.canvasHandler.transactionHandler.save("moved");
    }
    if (target.superType === "element") {
      const { id } = target;
      const el = this.canvasHandler.elementHandler.findById(id);
      this.canvasHandler.elementHandler.setPosition(el, target);
    }
    if (target.type === "activeSelection") {
      this.canvasHandler.drawingHandler.setType("activeSelection")
    }
  };

  /**
   * Scaling event object
   *
   * @param {FabricEvent} opt
   */
  public scaling = (opt: FabricEvent) => {
    const { target } = opt as any;
    if (this.canvasHandler.interactionMode === "crop") {
      this.canvasHandler.cropHandler.resize(opt);
    }
    // TODO...this.canvasHandler.guidelineHandler.scalingGuidelines(target);
    if (target.superType === "element") {
      const { id, width, height } = target;
      const el = this.canvasHandler.elementHandler.findById(id);
      // update the element
      this.canvasHandler.elementHandler.setScaleOrAngle(el, target);
      this.canvasHandler.elementHandler.setSize(el, target);
      this.canvasHandler.elementHandler.setPosition(el, target);
      const video = target as VideoObject;
      if (video.type === "video" && video.player) {
        video.player.setPlayerSize(width, height);
      }
    }
    target.addWithUpdate();
    target.setCoords();
  };

  /**
   * Scaled event object
   *
   * @param {FabricEvent} opt
   */
  public scaled = (_opt: FabricEvent) => {
    const { target } = _opt as any;
    let group = target as FabricGroup;
    if (target && target.item(0).type === "ellipse") {
      group.addWithUpdate();
      group.setCoords();
    }
    if (!this.canvasHandler.transactionHandler.active) {
      this.canvasHandler.transactionHandler.save("scaled");
    }
  };

  /**
   * Rotating event object
   *
   * @param {FabricEvent} opt
   */
  public rotating = (opt: FabricEvent) => {
    const { target } = opt as any;
    if(target.type === "activeSelection"){
      const rows = target.getObjects("group");
      rows.forEach((row: { set: (arg0: { newAngle: any; }) => void; }) => {
        row.set({
          newAngle: target.angle,
        });
      });
      return;
    }
    target.set({
      newAngle: target.angle,
    });
    if (target.superType === "element") {
      const { id } = target;
      const el = this.canvasHandler.elementHandler.findById(id);
      // update the element
      this.canvasHandler.elementHandler.setScaleOrAngle(el, target);
    }
  };

  /**
   * Rotated event object
   *
   * @param {FabricEvent} opt
   */
  public rotated = (event: FabricEvent) => {
    if (!this.canvasHandler.transactionHandler.active) {
      this.canvasHandler.transactionHandler.save("rotated");
    }
    if (this.canvasHandler.interactionMode === "selection") {
      this.canvasHandler.drawingHandler.onRotated(event);
    }
  };

  /**
   * Moing object at keyboard arrow key down event
   *
   * @param {KeyboardEvent} e
   * @returns
   */
  public arrowMoving = (e: KeyboardEvent) => {
    const activeObject = this.canvasHandler.canvas.getActiveObject() as FabricObject;
    if (!activeObject) {
      return false;
    }
    if (activeObject.id === "workarea") {
      return false;
    }
    if (e.code === code.ARROW_UP) {
      activeObject.set("top", activeObject.top - 2);
      activeObject.setCoords();
      this.canvasHandler.canvas.renderAll();
      return true;
    } else if (e.code === code.ARROW_DOWN) {
      activeObject.set("top", activeObject.top + 2);
      activeObject.setCoords();
      this.canvasHandler.canvas.renderAll();
      return true;
    } else if (e.code === code.ARROW_LEFT) {
      activeObject.set("left", activeObject.left - 2);
      activeObject.setCoords();
      this.canvasHandler.canvas.renderAll();
      return true;
    } else if (e.code === code.ARROW_RIGHT) {
      activeObject.set("left", activeObject.left + 2);
      activeObject.setCoords();
      this.canvasHandler.canvas.renderAll();
      return true;
    }
    if (this.canvasHandler.onModified) {
      this.canvasHandler.onModified(activeObject);
    }
    return true;
  };

  /**
   * Zoom at mouse wheel event
   *
   * @param {FabricEvent<WheelEvent>} opt
   * @returns
   */
  public mouseWheel = (opt: FabricEvent) => {
    const event = opt as FabricEvent<WheelEvent>;
    const { zoomEnabled } = this.canvasHandler;
    if (!zoomEnabled || !event.e.ctrlKey) {
      return;
    }

    const delta = event.e.deltaY;
    if (delta > 0) {
      this.canvasHandler.zoomHandler.zoomOut();
    } else {
      this.canvasHandler.zoomHandler.zoomIn();
    }

    if (this.canvasHandler.onZoom) {
      this.canvasHandler.onZoom(this.canvasHandler.canvas.getZoom());
    }

    event.e.preventDefault();
    event.e.stopPropagation();
  };

  /**
   * Mouse down event on object
   *
   * @param {FabricEvent<MouseEvent>} opt
   * @returns
   */
  public mouseDown = (opt: FabricEvent) => {
    const { editable } = this.canvasHandler;
    this.panning = this.canvasHandler.enablePanning;
    if (editable) {
      const event = opt as FabricEvent<MouseEvent>;
      const { target, subTargets } = event;

      if (event.e.altKey) {
        this.canvasHandler.interactionHandler.grab();
        this.panning = true;
        return;
      }

      if (this.canvasHandler.interactionMode === "grab") {
        this.panning = true;
        return;
      }

      this.canvasHandler.guidelineHandler.viewportTransform = this.canvasHandler.canvas.viewportTransform;
      this.canvasHandler.guidelineHandler.zoom = this.canvasHandler.canvas.getZoom();
      if (this.canvasHandler.interactionMode === "selection") {
        if (target) {
          if (target.superType === "polygonZone") {
            if (subTargets.length === 3) {
              this.canvasHandler.canvas.setActiveObject(subTargets[2]);
              this.canvasHandler.selectedTarget = subTargets[2];
              this.canvasHandler.drawingHandler.setType(subTargets[2].superType);
              return;
            }
          }
          this.canvasHandler.selectedTarget = target;
          this.canvasHandler.drawingHandler.setType(target.superType);
          return;
        }
      }
      if (this.canvasHandler.interactionMode === "seatSelection") {
        if (subTargets && subTargets[1] && subTargets[1].superType === "seat" && subTargets[1].selectable) {
          this.canvasHandler.canvas.setActiveObject(subTargets[1]);
          this.canvasHandler.canvas.requestRenderAll();
          this.canvasHandler.drawingHandler.setType(target.superType);
        }
        return;
      }

      this.canvasHandler.originPointer = this.canvasHandler.canvas.getPointer(event.e);
      if (this.canvasHandler.interactionMode === "drawing") {
        this.canvasHandler.canvas.discardActiveObject();
        this.canvasHandler.drawingHandler.onMousedown(event);
      }
    } else {
      const event = opt.e as MouseEvent | TouchEvent;

      const point =
        "touches" in event
          ? event.touches[0]
          : event;

      this.startX = point.clientX;
      this.startY = point.clientY;

      this.hasMoved = false;
    }
  };

  public mouseDblClick = (opt: FabricEvent) => {
    const { target } = opt;
    if (!target) return;
    if (target.superType.includes("Zone")) {
      this.canvasHandler.isZoneActive = true;
      this.canvasHandler.drawingHandler.setType(target.superType);
      this.canvasHandler.drawingHandler.onMouseDblClick(opt);
    }
  }
  /**
   * Mouse move event on canvas
   *
   * @param {FabricEvent<MouseEvent>} opt
   * @returns
   */
  public mouseMove = (opt: FabricEvent) => {
    const event = opt as FabricEvent<MouseEvent | TouchEvent>;
    if (this.canvasHandler.editable) {
      if (this.canvasHandler.interactionMode === "grab" && this.panning) {
        this.canvasHandler.interactionHandler.moving(event.e);
        this.canvasHandler.canvas.requestRenderAll();
        return;
      }
      if (this.canvasHandler.interactionMode === "drawing") {
        this.canvasHandler.drawingHandler.onMousemove(event);
      }
    } else {
      if (
        !this.panning &&
        this.canvasHandler.isInteractionEnabled &&
        !("touches" in event.e && event.e.touches.length > 1) &&
        !this.canvasHandler.isMultipleSelectionEnabled
      ) {
        this.objectHoverUpdate(event as FabricEvent<MouseEvent | TouchEvent>);
      }

      if (this.panning) {
        const canvas = this.canvasHandler.canvas;
        const p = "touches" in event.e ? event.e.touches[0] : event.e;

        const dx = p.clientX - this.startX;
        const dy = p.clientY - this.startY;

        if (!this.hasMoved && Math.hypot(dx, dy) > this.MOVE_THRESHOLD) {
          this.hasMoved = true;
          canvas.skipTargetFind = true;
          canvas.selection = false;
        }

        if (this.hasMoved) {
          const isModalActive = !!document.querySelector(".is_mobile_modal");
          if (!isModalActive) {
            this.cancelObjectHover();
          }

          this.canvasHandler.interactionHandler.moving(event.e);
          this.limitViewport();
          canvas.requestRenderAll();

          return;
        }
      }
    }
  };

  /**
  * Llama esto cuando el mouse sale del canvas o del objeto para ocultar el hover/popup inmediatamente
  */
  public cancelObjectHover = () => {
    if (this.objectHoverTimeout != null) {
      clearTimeout(this.objectHoverTimeout);
      this.objectHoverTimeout = null;
    }
    // Desactiva el hover en todos los modos relevantes
    switch (this.canvasHandler.interactionMode) {
      case "selector":
        this.canvasHandler.selectorHandler.deactivateObjectHover();
        break;
      case "blocker":
        this.canvasHandler.blockerHandler.deactivateObjectHover();
        break;
      case "channel":
        this.canvasHandler.channelHandler.deactivateObjectHover();
        break;
      case "detail":
        this.canvasHandler.detailHandler.deactivateObjectHover();
        break;
      case "preview":
        this.canvasHandler.previewHandler.deactivateObjectHover();
        break;
      default:
        break;
    }
  };

  /**
   * Espacio de coordenadas "absolute" / aCoords (sin viewport): el mismo que usa
   * Object#calcCoords(true) y getBoundingRect(true). Debe coincidir con el círculo del asiento.
   */
  // moved to ObjectUtil.ts

  private objectHoverUpdate = (event: FabricEvent<MouseEvent | TouchEvent>) => {
    if (this.objectHoverTimeout != null) {
      clearTimeout(this.objectHoverTimeout);
      this.objectHoverTimeout = null;
    }
    this.objectHoverEvent = event;
    this.objectHoverTimeout = window.setTimeout(() => {
      this.objectHoverTimeout = null;
      if (this.objectHoverEvent) {
        const enriched = enrichObjectEvent(this.canvasHandler.canvas, this.objectHoverEvent);
        this.applyObjectHoverFromEvent(enriched);
      }
    }, 5);
  };

  private applyObjectHoverFromEvent = (event: FabricEvent<MouseEvent | TouchEvent>) => {
    switch (this.canvasHandler.interactionMode) {
      case "selector":
        this.canvasHandler.selectorHandler.updateObjectHoverState(event);
        break;
      case "blocker":
        this.canvasHandler.blockerHandler.updateObjectHoverState(event);
        break;
      case "channel":
        this.canvasHandler.channelHandler.updateObjectHoverState(event);
        break;
      case "detail":
        this.canvasHandler.detailHandler.updateObjectHoverState(event);
        break;
      case "preview":
        this.canvasHandler.previewHandler.updateObjectHoverState(event);
        break;
      default:
        break;
    }
  };

  /**
   * Mouse up event on canvas
   *
   * @param {FabricEvent<MouseEvent>} opt
   * @returns
   */
  public mouseUp = (opt: FabricEvent) => {
    const event = opt as FabricEvent<MouseEvent | TouchEvent>;
    const isTouch = isTouchInteraction(event);

    if (this.canvasHandler.editable) {
      if (this.canvasHandler.interactionMode === "seatSelection") {
        // this.canvasHandler.canvas.discardActiveObject();
      }
      if (this.canvasHandler.interactionMode === "grab") {
        this.panning = false;
        return;
      }

      if (this.canvasHandler.interactionMode === "drawing") {
        this.canvasHandler.drawingHandler.onMouseup(event);
      }

      if (this.canvasHandler.guidelineOption.enabled) {
        this.canvasHandler.guidelineHandler.verticalLines.length = 0;
        this.canvasHandler.guidelineHandler.horizontalLines.length = 0;
      }

      this.canvasHandler.canvas.renderAll();
    } else {
      let { target } = opt;

      // Recuperar target (especialmente útil en touch cuando el asiento aún es pequeño en pantalla)
      if (!target) {
        target = this.findBestTargetForPointer(event, this.canvasHandler.canvas.skipTargetFind);
      }

      const clickEvent = enrichObjectEvent(
        this.canvasHandler.canvas,
        { ...event, target } as FabricEvent<MouseEvent | TouchEvent>
      );

      const draggedForSelection = wasDraggedForSelection(
        opt,
        this.canvasHandler.isMultipleSelectionEnabled,
        this.startX,
        this.startY
      );

      // Evitamos el zoom si se dibujó el rectángulo de selección (draggedForSelection == true)
      const shouldZoom = !this.hasMoved && !this.canvasHandler.isInteractionEnabled
        && !draggedForSelection
        && (isTouch || !!target)
        && this.canvasHandler.interactionMode !== "selection"
        && this.canvasHandler.interactionMode !== "seatSelection";

      if (shouldZoom) {
        const pointerRaw = this.canvasHandler.canvas.getPointer(event.e);
        const zoomPoint = new fabric.Point(pointerRaw.x, pointerRaw.y);
        this.canvasHandler.zoomHandler.zoomToCenterAtPoint(zoomPoint);
      } else if (draggedForSelection) {
        const selectionRect = getDraggingSelectionRect(opt, this.canvasHandler.canvas, this.startX, this.startY);
        if (selectionRect) {
          switch (this.canvasHandler.interactionMode) {
            case "blocker":
              this.canvasHandler.blockerHandler.handleSelectObjects({ ...opt, selectionRect } as any);
              break;
            case "channel":
              this.canvasHandler.channelHandler.handleSelectObjects({ ...opt, selectionRect } as any);
              break;
            default:
              break;
          }
        }
      } else if (!this.hasMoved && target) {
        switch (this.canvasHandler.interactionMode) {
          case "selector":
            this.canvasHandler.selectorHandler.handleClickObject(clickEvent);
            break;
          case "blocker":
            if (!this.canvasHandler.isMultipleSelectionEnabled)
              this.canvasHandler.blockerHandler.handleClickObject(clickEvent);
            break;
          case "channel":
            if (!this.canvasHandler.isMultipleSelectionEnabled)
              this.canvasHandler.channelHandler.handleClickObject(clickEvent);
            break;
          case "detail":
            this.canvasHandler.detailHandler.handleClickObject(clickEvent);
            break;
          default:
            break;
        }
      }

      this.hasMoved = false;
      this.panning = false;
      this.canvasHandler.canvas.skipTargetFind = this.canvasHandler.isMultipleSelectionEnabled;
      this.canvasHandler.canvas.selection = true;
      if (!target && isTouch) {
        const isModalActive = !!document.querySelector(".is_mobile_modal");
        if (!isModalActive) {
          this.cancelObjectHover();
        }
      }
    }
  };

  private findBestTargetForPointer = (
    event: FabricEvent<MouseEvent | TouchEvent>,
    restoreSkipTargetFind: boolean
  ): fabric.Object | undefined => {
    const canvas = this.canvasHandler.canvas;
    const previousSkipTargetFind = canvas.skipTargetFind;
    const previousTolerance = (canvas as any).targetFindTolerance ?? 0;
    const isTouch = isTouchInteraction(event as unknown as FabricEvent);

    try {
      canvas.skipTargetFind = false;
      if (isTouch) {
        // En mobile ampliamos el área de hit-test para facilitar tap sobre asientos.
        (canvas as any).targetFindTolerance = Math.max(previousTolerance, 24);
      }
      return canvas.findTarget(event.e as Event, false) as fabric.Object;
    } finally {
      (canvas as any).targetFindTolerance = previousTolerance;
      canvas.skipTargetFind = restoreSkipTargetFind ?? previousSkipTargetFind;
    }
  };

  /**
   * Selection event event on canvas
   *
   * @param { FabricEvent } opt
   */
  public selection = (opt: FabricEvent) => {
    if (this.canvasHandler.enablePanning) {
      this.canvasHandler.canvas.discardActiveObject();
      return;
    }
    const { onSelect, activeSelectionOption } = this.canvasHandler;
    const target = opt.target as FabricObject<fabric.ActiveSelection>;
    const isActiveSelection = opt.selected?.length > 1;
    if (target && isActiveSelection) {
      if (this.canvasHandler.interactionMode === "seatSelection") {
        this.canvasHandler.canvas.discardActiveObject();
        return;
      }
      target.set({
        ...activeSelectionOption,
      });
      target.setControlsVisibility({
        tl: false, //top-left
        mt: false, // middle-top
        tr: false, //top-right
        ml: false, //middle-left
        mr: false, //middle-right
        bl: false, // bottom-left
        mb: false, //middle-bottom
        br: false, //bottom-right
        mtr: true,
      });

      switch (this.canvasHandler.interactionMode) {
        case "selection":
          target.type = "activeSelection";
          this.canvasHandler.selectedTarget = target;
          this.canvasHandler.drawingHandler.setType("activeSelection");
          break;
        default:
          break;
      }
    }
    if (onSelect) {
      onSelect(target);
    }
  };

  /**
   * Called resize event on canvas
   *
   * @param {number} nextWidth
   * @param {number} nextHeight
   * @returns
   */
  public resize = (
    nextWidth: number,
    nextHeight: number,
  ) => {
    this.canvasHandler.canvas.setWidth(nextWidth).setHeight(nextHeight);
    this.canvasHandler.canvas.setBackgroundColor(
      this.canvasHandler.canvasOption.backgroundColor,
      this.canvasHandler.canvas.renderAll.bind(this.canvasHandler.canvas)
    );
    if (!this.canvasHandler.workarea) {
      return;
    }
    const diffWidth = nextWidth / 2 - this.canvasHandler.width / 2;
    const diffHeight = nextHeight / 2 - this.canvasHandler.height / 2;
    this.canvasHandler.width = nextWidth;
    this.canvasHandler.height = nextHeight;
    if (this.canvasHandler.workarea.layout === "fixed") {
      if (this.canvasHandler.gridOption.enabled) {
        return;
      }
      this.canvasHandler.canvas.requestRenderAll();
      return;
    }
    if (this.canvasHandler.workarea.layout === "responsive") {
      const { scaleX } = this.canvasHandler.workareaHandler.calculateScale();
      const center = this.canvasHandler.canvas.getCenter();
      const deltaPoint = new fabric.Point(diffWidth, diffHeight);
      this.canvasHandler.canvas.relativePan(deltaPoint);
      this.canvasHandler.zoomHandler.zoomToPoint(
        new fabric.Point(center.left, center.top),
        scaleX
      );
      return;
    }
    const scaleX = nextWidth / this.canvasHandler.workarea.width;
    const scaleY = nextHeight / this.canvasHandler.workarea.height;
    const diffScaleX =
      nextWidth / (this.canvasHandler.workarea.width * this.canvasHandler.workarea.scaleX);
    const diffScaleY =
      nextHeight /
      (this.canvasHandler.workarea.height * this.canvasHandler.workarea.scaleY);
    this.canvasHandler.workarea.set({
      scaleX,
      scaleY,
    });
    this.canvasHandler.canvas.getObjects().forEach((obj: any) => {
      if (obj.id !== "workarea") {
        const left = obj.left * diffScaleX;
        const top = obj.top * diffScaleY;
        const newScaleX = obj.scaleX * diffScaleX;
        const newScaleY = obj.scaleY * diffScaleY;
        obj.set({
          scaleX: newScaleX,
          scaleY: newScaleY,
          left,
          top,
        });
        obj.setCoords();
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  /**
   * Paste event on canvas
   *
   * @param {ClipboardEvent} e
   * @returns
   */
  public paste = async (e: ClipboardEvent) => {
    if (this.canvasHandler.canvas.wrapperEl !== document.activeElement) {
      return false;
    }
    if (e.preventDefault) {
      e.preventDefault();
    }
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    const clipboardData = e.clipboardData;
    if (clipboardData.types.length) {
      clipboardData.types.forEach((clipboardType: string) => {
        if (clipboardType === "text/plain") {
          const textPlain = clipboardData.getData("text/plain");
          try {
            const objects = JSON.parse(textPlain);
            const {
              gridOption: { grid = 10 },
              isCut,
            } = this.canvasHandler;
            const padding = isCut ? 0 : grid;
            if (objects && Array.isArray(objects)) {
              const filteredObjects = objects.filter((obj) => obj !== null);
              if (filteredObjects.length === 1) {
                const obj = filteredObjects[0];
                if (typeof obj.cloneable !== "undefined" && !obj.cloneable) {
                  return;
                }
                obj.left = obj.properties.left + padding;
                obj.top = obj.properties.top + padding;
                const createdObj = this.canvasHandler.add(obj, false, true);
                this.canvasHandler.canvas.setActiveObject(createdObj as FabricObject);
                this.canvasHandler.canvas.requestRenderAll();
              } else {
                const nodes = [] as any[];
                const targets = [] as any[];
                objects.forEach((obj) => {
                  if (!obj) {
                    return;
                  }
                  if (obj.superType === "link") {
                    obj.fromNodeId = nodes[obj.fromNodeIndex].id;
                    obj.toNodeId = nodes[obj.toNodeIndex].id;
                  } else {
                    obj.left = obj.properties.left + padding;
                    obj.top = obj.properties.top + padding;
                  }
                  const createdObj = this.canvasHandler.add(obj, false, true);
                  if (obj.superType === "node") {
                    nodes.push(createdObj);
                  } else {
                    targets.push(createdObj);
                  }
                });
                const activeSelection = new fabric.ActiveSelection(
                  nodes.length ? nodes : targets,
                  {
                    canvas: this.canvasHandler.canvas,
                    ...this.canvasHandler.activeSelectionOption,
                  }
                );
                this.canvasHandler.canvas.setActiveObject(activeSelection);
                this.canvasHandler.canvas.requestRenderAll();
              }
              if (!this.canvasHandler.transactionHandler.active) {
                this.canvasHandler.transactionHandler.save("paste");
              }
              this.canvasHandler.isCut = false;
              this.canvasHandler.copy();
            }
          } catch (error) {
            console.error(error);
            // const item = {
            //     id: uuv4id(),
            //     type: 'textbox',
            //     text: textPlain,
            // };
            // this.canvasHandler.add(item, true);
          }
        } else if (clipboardType === "text/html") {
          // Todo ...
          // const textHtml = clipboardData.getData('text/html');
          // console.log(textHtml);
        } else if (clipboardType === "Files") {
          // Array.from(clipboardData.files).forEach((file) => {
          //     const { type } = file;
          //     if (type === 'image/png' || type === 'image/jpeg' || type === 'image/jpg') {
          //         const item = {
          //             id: v4(),
          //             type: 'image',
          //             file,
          //             superType: 'image',
          //         };
          //         this.canvasHandler.add(item, true);
          //     } else {
          //         console.error('Not supported file type');
          //     }
          // });
        }
      });
    }
    return true;
  };

  /**
   * Keydown event on document
   *
   * @param {KeyboardEvent} e
   */
  public keyDown = (e: KeyboardEvent) => {
    const { keyEvent, editable } = this.canvasHandler;
    if (!Object.keys(keyEvent).length) {
      return;
    }
    this.canvasHandler.deleteIncompletePolygon();
    const { clipboard } = keyEvent;
    if (this.canvasHandler.interactionHandler.isDrawingMode()) {
      if (this.canvasHandler.shortcutHandler.isEscape(e)) {
        // if (this.canvasHandler.selectedObject === "polygon") {
        //    this.canvasHandler.shapeHandler.polygon.finish();
        // } 
      }
      return;
    }
    if (this.canvasHandler.interactionMode === "seatSelection") {
      return;
    }
    if (this.canvasHandler.shortcutHandler.isW(e)) {
      this.code = e.code;
      this.canvasHandler.interactionHandler.grab();
      return;
    }
    if (e.altKey && editable) {
      this.canvasHandler.interactionHandler.grab();
      return;
    }
    if (this.canvasHandler.shortcutHandler.isEscape(e)) {
      if (this.canvasHandler.interactionMode === "selection") {
        this.canvasHandler.canvas.discardActiveObject();
        this.canvasHandler.canvas.renderAll();
      }
      this.canvasHandler.tooltipHandler.hide();
    }
    if (this.canvasHandler.canvas.wrapperEl !== document.activeElement) {
      return;
    }
    if (editable) {
      if (this.canvasHandler.shortcutHandler.isQ(e)) {
        this.code = e.code;
      } else if (this.canvasHandler.shortcutHandler.isDelete(e)) {
        this.canvasHandler.remove();
      } else if (this.canvasHandler.shortcutHandler.isArrow(e)) {
        this.arrowMoving(e);
      } else if (this.canvasHandler.shortcutHandler.isCtrlA(e)) {
        e.preventDefault();
        this.canvasHandler.selectAll();
      } else if (this.canvasHandler.shortcutHandler.isCtrlC(e)) {
        e.preventDefault();
        this.canvasHandler.copy();
      } else if (this.canvasHandler.shortcutHandler.isCtrlV(e) && !clipboard) {
        e.preventDefault();
        this.canvasHandler.paste();
      } else if (this.canvasHandler.shortcutHandler.isCtrlX(e)) {
        e.preventDefault();
        this.canvasHandler.cut();
      } else if (this.canvasHandler.shortcutHandler.isCtrlZ(e)) {
        e.preventDefault();
        this.canvasHandler.transactionHandler.undo();
      } else if (this.canvasHandler.shortcutHandler.isCtrlY(e)) {
        e.preventDefault();
        this.canvasHandler.transactionHandler.redo();
      } else if (this.canvasHandler.shortcutHandler.isPlus(e)) {
        e.preventDefault();
        this.canvasHandler.zoomHandler.zoomIn();
      } else if (this.canvasHandler.shortcutHandler.isMinus(e)) {
        e.preventDefault();
        this.canvasHandler.zoomHandler.zoomOut();
      } else if (this.canvasHandler.shortcutHandler.isO(e)) {
        e.preventDefault();
        this.canvasHandler.zoomHandler.zoomOneToOne();
      } else if (this.canvasHandler.shortcutHandler.isP(e)) {
        e.preventDefault();
        this.canvasHandler.zoomHandler.zoomToFit();
      }
      return;
    }
    return;
  };

  /**
   * Key up event on canvas
   *
   * @param {KeyboardEvent} _e
   */
  public keyUp = (e: KeyboardEvent) => {
    if (this.canvasHandler.interactionHandler.isDrawingMode() || !this.canvasHandler.editable) {
      return;
    }
    if (!this.canvasHandler.shortcutHandler.isW(e)) {
      this.canvasHandler.interactionHandler.selection();
    }
  };

  /**
   * Context menu event on canvas
   *
   * @param {MouseEvent} e
   */
  public contextMenu = (e: MouseEvent) => {
    if (this.canvasHandler.interactionMode === "seatSelection") {
      return;
    }
    e.preventDefault();
    const { editable, onContext } = this.canvasHandler;
    if (editable && onContext) {
      const target = this.canvasHandler.canvas.findTarget(e, false) as FabricObject;
      if (target && target.type !== "activeSelection") {
        this.canvasHandler.select(target);
      }
      this.canvasHandler.contextmenuHandler.show(e, target);
    }
  };

  private onTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();

      const t1 = e.touches.item(0);
      const t2 = e.touches.item(1);

      if (!t1 || !t2) return;
      this.isPinching = true;
      this.pinchZoomStartDistance = this.getDistance(t1, t2);
      this.pinchZoomStartZoom = this.canvasHandler.canvas.getZoom();
    }
  };

  private onTouchMove = (e: TouchEvent) => {
    if (!this.isPinching || e.touches.length !== 2) return;

    e.preventDefault();

    const t1 = e.touches.item(0);
    const t2 = e.touches.item(1);

    if (!t1 || !t2) return;
    const newDistance = this.getDistance(t1, t2);
    const zoomFactor = newDistance / this.pinchZoomStartDistance;

    const zoom = this.pinchZoomStartZoom * zoomFactor;

    const mid = this.getMidPoint(t1, t2);
    const point = new fabric.Point(mid.x, mid.y);

    this.canvasHandler.zoomHandler.zoomToPoint(point, zoom);
  };

  private onTouchEnd = () => {
    this.isPinching = false;
  };

  getDistance = (t1: Touch, t2: Touch) => {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  getMidPoint = (t1: Touch, t2: Touch) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  /**
   * Mouse down event on canvas
   *
   * @param {MouseEvent} _e
   */
  public onMouseDown = (_e: MouseEvent) => {
    this.canvasHandler.contextmenuHandler.hide();
  };

  private limitViewport() {
    const canvas = this.canvasHandler.canvas;
    const vpt = canvas.viewportTransform;
    const zoom = vpt[0];

    const bounds = this.canvasHandler.viewportBounds;
    if (!bounds) return;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const margin = 50; // px de margen por cada lado

    // bounds + margen
    const left = bounds.left - margin;
    const top = bounds.top - margin;
    const width = bounds.width + margin * 2;
    const height = bounds.height + margin * 2;

    const contentWidth = width * zoom;
    const contentHeight = height * zoom;

    // ===== X =====
    if (contentWidth <= canvasWidth) {
      vpt[4] =
        canvasWidth / 2 -
        (left + width / 2) * zoom;
    } else {
      const minX = canvasWidth - (left + width) * zoom;
      const maxX = -left * zoom;
      vpt[4] = Math.min(maxX, Math.max(minX, vpt[4]));
    }

    // ===== Y =====
    if (contentHeight <= canvasHeight) {
      vpt[5] =
        canvasHeight / 2 -
        (top + height / 2) * zoom;
    } else {
      const minY = canvasHeight - (top + height) * zoom;
      const maxY = -top * zoom;
      vpt[5] = Math.min(maxY, Math.max(minY, vpt[5]));
    }

    canvas.setViewportTransform(vpt);
  }
}

export default EventHandler;
